import axios from 'axios';
import {Advisor,BrokerDealer,PBGCRates,ProposalPricingSection,ProposalPricingDetail} from './dataModels.js';
import {getAuthHeader} from '../global_helpers/authHeader.js';
import {getTPAID, uuidv4} from '../global_helpers/sessionReturn.js';
import {safeInt} from '../global_helpers/utility.js';

//######################################################
//### The ___Section classes represent the different 
//### parts of the proposal builder page.
//######################################################

export class OpportunitySection {
    constructor(data = {}){
        this.placeholder = data.placeholder || null;
    }
}

export class PlanTypeSection {
    constructor(data = {}){
        //database properties
        this.Id = data.plan_type_id || null;
        this.ParentType = data.parent_type || null;
        this.SubType = data.sub_type || null;
        this.IsMEP = data.is_MEP || false;
        this.IsNoRevenueShare = data.is_no_revenue_share || false;
        this.IsTPAChange = data.is_tpa_change || false;

        //ui properties
        this.ParentTypes = ['401(K)', '403(B)', 'DB/DC Combo', 'Cash Balance Only'];
        this.SubTypes = ['Startup Non-Safe Harbor','Startup Safe Harbor','Takeover'];
        this.DisableSubType = true;
        this.DisableMEP = true;
        this.DisableNoRevShare = true;
        this.DisableTPAChange = true;

        this.displayName = "";

        this.setParentType(this.ParentType);
    }
    setParentType(type){
        // console.log(`PlanTypeSection.setParentType type: ${type}`);
        switch(type){
            case '401(K)':
            case '403(B)':
            case 'DB/DC Combo':
                // console.log(`PlanTypeSection.setParentType type: 401(K), 403(B), DB/DC Combo`);
                this.DisableSubType = false;
                if (this.SubType) {
                    this.DisableMEP = false;
                    this.DisableNoRevShare = false;
                    this.DisableTPAChange = false;
                } else {
                    this.DisableMEP = true;
                    this.DisableNoRevShare = true;
                }
                break;
            case  'Cash Balance Only':
                // console.log(`PlanTypeSection.setParentType type: cash`);
                this.DisableSubType = true;
                this.SubType = null;
                this.DisableMEP = true;
                this.IsMEP = false;
                this.DisableNoRevShare = true;
                this.IsNoRevenueShare = false;
                this.DisableTPAChange = true;
                this.IsTPAChange = false;
                break;
            default:
                // console.log(`PlanTypeSection.setParentType type: default`);
                break;
        }
        this.setDisplayName();
    }
    setDisplayName(cbType = false){
        let a = '';
        if (this.SubType){
            a = ` ${this.SubType}`;
        }
        let b = '';
        if (this.IsMEP){
            b = ' - MEP';
        }
        let c = '';
        if (cbType){
            c = ` - ${cbType}`;
        }
        this.displayName = `${this.ParentType}${a}${b}${c}`;
    }
    get isComplete(){
        if (this.ParentType == 'Cash Balance Only') return true;
        if (this.ParentType != null && this.SubType != null) return true;
        return false;
    }
    get isCBType(){
        return ['DB/DC Combo', 'Cash Balance Only'].includes(this.ParentType);
    }
    get isCBOnly(){
        return this.ParentType == 'Cash Balance Only';
    }
    get planTypeForPricingSelection(){
        if (this.ParentType == 'Cash Balance Only') return 'cashbalance';
        switch(this.SubType){
            case 'Startup Non-Safe Harbor':
                return 'startup-nsh'
            case 'Startup Safe Harbor':
                return 'startup-sh'
            case 'Takeover':
                return 'takeover'
            default:
                return 'takeover'
        }
    }
    toJson(){
        return {
            'plan_type_id': this.Id,
            'parent_type': this.ParentType,
            'sub_type': this.SubType,
            'is_MEP': this.IsMEP,
            'is_no_rev_share': this.IsNoRevenueShare,
            'is_tpa_change': this.IsTPAChange
        }
    }
}

export class CashBalanceSection {
    constructor(data = {}){
        this.CBType = data.cb_type || null;
        this.Template = data.template || null;

        this.CBTypes = ['Pooled Brokerage Accounts','Pooled Cash Balance Investment Account'];
        this._planType = null;
    }
    reset(){
        this.CBType = null;
        this.Template = null;
        this._planType = null;
    }
    get isComplete(){
        if (this._planType == 'DB/DC Combo') {
            return this.CBType != null;
        } else if (this._planType == 'Cash Balance Only') {
            return this.CBType != null && this.Template != null;
        }
        return false;
    }
    get packet(){
        return {
            "rk_id": null,
            "rk_name": "Cash Balance",
            "wholesaler_id": null,
            "template_id": this.Template.template_id || this.Template,
            "rep_user_id": null,
            "rep_name": "",
            "rep_email": ""
        }
    }
    toJson(){
        return {
            'cash_balance_type': this.CBType,
            'template': this.Template
        }
    }
}

export class TemplateSection {
    constructor(data = {}){
        this.Template = data.template || null;
    }
    get isComplete(){
        return this.Template != null;
    }
    get packet(){
        return {
            "rk_id": null,
            "rk_name": "No Revenue Share",
            "wholesaler_id": null,
            "template_id": this.Template?.template_id || this.Template,
            "rep_user_id": null,
            "rep_name": "",
            "rep_email": ""
        }
    }
    reset(){
        this.Template = null;
    }
    toJson(){
        return {
            'template': this.Template
        }
    }
}

export class RevShareSection {
    constructor(data = {}){
        this.multiQuote = data.multiQuote || false;
        this.revSharePrograms = [];
        this.selected_programs = data.selected_programs || [];
    }
    reset(){
        this.multiQuote = false;
        this.selected_programs = [];
    }
    get isComplete(){
        if (this.multiQuote){
            return this.selected_programs.length > 0;
        } else {
            return typeof(this.selected_programs.rk_id) !== 'undefined';
        }
        
    }
    get rk_ids(){
        let result = [];
        // console.log(this.selected_programs)
        if (this.multiQuote){
            this.selected_programs.forEach(p => {
                result.push(p.rk_id);
            })
        } else {
            result.push(this.selected_programs.rk_id);
        }
        return result;
    }
    getPackets(wholesalerTemplates){
        let result = [];
        // console.log(wholesalerTemplates)
        if (this.multiQuote){
            this.selected_programs.forEach(p => {
                wholesalerTemplates.forEach(wt => {
                // console.log(wt);
                    if (wt.rk_ids.includes(p.rk_id)) {
                        result.push({
                            "rk_id": p.rk_id,
                            "rk_name": p.rk_name,
                            "wholesaler_id": wt.wholesaler_id,
                            "template_id": wt.selected_template.template_id || wt.selected_template,
                            "rep_user_id": wt.selected_rep_user.rep_user_id,
                            "rep_name": wt.selected_rep_user.rep_name,
                            "rep_email": wt.selected_rep_user.rep_email
                        })
                    }
                });
            });
        } else {
            wholesalerTemplates.forEach(wt => {
                if (wt.rk_ids.includes(this.selected_programs.rk_id)) {
                    // console.log(wt);
                    result.push({
                        "rk_id": this.selected_programs.rk_id,
                        "rk_name": this.selected_programs.rk_name,
                        "wholesaler_id": wt.wholesaler_id,
                        "template_id": wt.selected_template.template_id || wt.selected_template,
                        "rep_user_id": wt.selected_rep_user.rep_user_id,
                        "rep_name": wt.selected_rep_user.rep_name,
                        "rep_email": wt.selected_rep_user.rep_email
                    })
                }
            });
        }
        return result;
    }
}

export class ProviderSection {
    constructor(){
        this.wholesalerTemplates = [];
        this.defaultTemplate = {};
    }
    load(wts){
        // console.log(wts);
        this.wholesalerTemplates = [];
        wts.forEach(wt => {
            let p = new WholesalerTemplate(wt);
            // console.log(p);
            if ( p.selected_template === null ) p.selected_template = this.defaultTemplate;
            this.wholesalerTemplates.push(p);
        });
    }
    reset(){
        this.wholesalerTemplates = [];
        this.defaultTemplate = {};
    }
    get isComplete(){
        let result = true;
        if (this.wholesalerTemplates.length === 0) return false;
        this.wholesalerTemplates.forEach(wt => {
            if (wt.selected_template == null || wt.selected_rep_user == null) result = false;
        })
        return result;
    }
}
    
export class WholesalerTemplate {
    constructor(data = {}){
        this.rk_ids = data.rk_ids || [];
        this.wholesaler_id = data.wholesaler_id || null;
        this.wholesaler_name = data.wholesaler_name ||  null;
        this.selected_rep_user = data.selected_rep_user || null;
        this.selected_template = data.selected_template || null;

        this.rep_users = data.rep_users || [];
    }
}

export class BrokerSection {
    constructor(data = {}){
        this.radioSelected = data.selected_broker || new BrokerDealer();
        this.dropDownSelected = null;

        this.is_no_broker = false;
        this.no_broker_dealer = new BrokerDealer();

        this.radioOptions = [];
        this.dropDownOptions = [];

        this.confirmNoBroker = false;
    }
    get isComplete(){
        if (this.is_no_broker) return true;
        return this.radioSelected.broker_id !== null && typeof(this.radioSelected) !== "string";
    }
    get defaultIds(){
        let defaults = [];
        this.radioOptions.forEach(broker => {
           if (broker.is_default) defaults.push(broker.broker_id);
        });
        return defaults;
    }
    get disableForm(){
        return this.radioSelected.broker_dealer_name == null;
    }
    get brokerId(){
        if (this.is_no_broker){
            return this.no_broker_dealer.broker_id;
        } else {
            return this.radioSelected.broker_id;
        }
    }
    reset(){
        this.radioSelected = new BrokerDealer();
    }
    moveSelectedDDLOption(){
        this.dropDownSelected.is_default = true;
        let i = this.dropDownOptions.indexOf(this.dropDownSelected);
        this.radioOptions.push(this.dropDownSelected)
        this.radioSelected = this.dropDownSelected
        this.dropDownSelected = null;
        this.dropDownOptions.splice(i,1);
    }
    loadOptions(data){
        data.forEach(broker => {
            if (broker.broker_dealer_name === "No Broker Dealer"){
                this.no_broker_dealer = broker;
            } else if (broker.is_default){
                this.radioOptions.push(broker);
            } else {
                this.dropDownOptions.push(broker);
            }
        });
    }
}

export class AdvisorSection {
    constructor(data = {}){
        this.radioSelected = data.selected_advisor || new Advisor();
        this.dropDownSelected = null;

        this.radioOptions = [];
        this.dropDownOptions = [];

        this.brokerOptions = [];
        this.brokerSelected = new BrokerDealer();
        this.confirm = false;
    }
    get isComplete(){
        return this.radioSelected.advisor_name !== null; 
    }
    get disableForm(){
        return this.radioSelected.advisor_name == null;
    }
    loadBrokerOptions(data){
        data.forEach(broker => {
            this.brokerOptions.push({
                broker_dealer_name:broker.broker_dealer_name,
                broker_id:broker.broker_id
            });
        });
    }
    loadAdvisorOptions(data){
        data.forEach(advisor => {
            if (advisor.is_default){
                this.radioOptions.push(advisor);
            } else {
                this.dropDownOptions.push(advisor);
            }
        });
    }
    reset(){
        this.radioSelected = new Advisor();
        this.dropDownSelected = null;
        this.radioOptions = [];
        this.dropDownOptions = [];
        this.brokerSelected = new BrokerDealer();
        this.confirm = false;
    }
    async updateAdvisor(brokerId = null){
        // console.log('updateAdvisor called');
        let authHeader = getAuthHeader();
        this.radioSelected.tpa_id = getTPAID();
        if (brokerId) this.radioSelected.broker_id = brokerId;
        axios.put('/advisor/', this.radioSelected, authHeader);
    }
    async createAdvisor(broker_id){
        // console.log('createAdvisor called');
        let authHeader = getAuthHeader();
        let newAdvisor = new Advisor();
        newAdvisor.tpa_id = getTPAID();
        newAdvisor.broker_id = broker_id;
        newAdvisor.advisor_name = this.dropDownSelected;
        axios.post('/advisor/', newAdvisor, authHeader).then((res) => {
            this.radioSelected = res.data;
            this.radioOptions.push(this.radioSelected);
        });
    }
    async moveSelectedDDLOption(){
        // console.log('AdvisorSection.moveSelectedDDLOption called');
        this.dropDownSelected.is_default = true;
        let i = this.dropDownOptions.indexOf(this.dropDownSelected);
        this.radioOptions.push(this.dropDownSelected)
        this.radioSelected = this.dropDownSelected
        this.dropDownSelected = null;
        this.dropDownOptions.splice(i,1);

        let authHeader = getAuthHeader();
        axios.put('/advisor/', this.radioSelected, authHeader);
    }
}

export class PricingSection {
    constructor(){
        this.pricing_assumptions = new PricingAssumptions();
        this.standard_fees = new StandardFees();
        this.mep_pricing = new MEPPricing();
        this.plan_doc_maint = new PlanDocMaint();
        this.audit_fee = new AuditFee();
        this.three_sixteen = new ThreeSixteen();
        this.pbgc = new PBGC();
        this.payroll_integration = new PayrollIntegration();
        this.payroll_processing = new PayrollProcessing();
        this.custom_additions = new CustomAdditions();
        this.custom_notes = new CustomNotes();

        this.errors = [];
        this.is_takeover = false;
        this.isLoaded = false;
    }
    reset(){
        this.mep_pricing.reset();
        this.pricing_assumptions.reset();
    }
    get isComplete(){
        // console.log('PricingSection.isComplete');
        if (this.errors.length > 0) return false;
        let result = false;
        if (this.mep_pricing.is_shown && this.mep_pricing.is_checked && this.mep_pricing.isComplete(this.is_takeover)){
            result = true;
        } else {
            result = this.pricing_assumptions.isComplete(this.is_takeover);
        }
        return result;
    }
    loadData(data){
        let section = data.pricing_assumptions || {};
        this.pricing_assumptions = new PricingAssumptions(section);

        section = data.standard_fees || {};
        this.standard_fees = new StandardFees(section);
        
        section = data.mep_pricing || {};
        this.mep_pricing = new MEPPricing(section);
        
        section = data.plan_doc_maint || {};
        this.plan_doc_maint = new PlanDocMaint(section);
        
        section = data.audit_fee || {};
        this.audit_fee = new AuditFee(section);
        
        section = data.three_sixteen || {};
        this.three_sixteen = new ThreeSixteen(section);
        
        section = data.pbgc || {};
        this.pbgc = new PBGC(section);
        
        section = data.payroll_integration || {};
        this.payroll_integration = new PayrollIntegration(section);
        
        section = data.payroll_processing || {};
        this.payroll_processing = new PayrollProcessing(section);
        
        section = data.custom_additions || {};
        this.custom_additions = new CustomAdditions(section);
        
        section = data.custom_notes || {};
        this.custom_notes = new CustomNotes(section);
    }
    toJson(){
        // console.log('PricingSection.toJson called');
        return {
            'pricing_assumptions': this.pricing_assumptions.toJson(),
            'standard_fees': this.standard_fees.toJson(),
            'mep_pricing': this.mep_pricing.toJson(),
            'plan_doc_maint': this.plan_doc_maint.toJson(),
            'audit_fee': this.audit_fee.toJson(),
            'three_sixteen': this.three_sixteen.toJson(),
            'pbgc': this.pbgc.toJson(),
            'payroll_integration': this.payroll_integration.toJson(),
            'payroll_processing': this.payroll_processing.toJson(),
            'custom_additions': this.custom_additions.toJson(),
            'custom_notes': this.custom_notes.toJson(),
            // 'tpa_change': this.tpa_change.toJson(),
        };
    }
    detailsForDefaults(planType){
        let result = [];
        this.standard_fees.detailsForDefaults(planType).forEach(d => {result.push(d)});
        this.mep_pricing.detailsForDefaults(planType).forEach(d => {result.push(d)});
        this.plan_doc_maint.detailsForDefaults(planType).forEach(d => {result.push(d)});
        this.audit_fee.detailsForDefaults(planType).forEach(d => {result.push(d)});
        this.three_sixteen.detailsForDefaults(planType).forEach(d => {result.push(d)});
        this.pbgc.detailsForDefaults(planType).forEach(d => {result.push(d)});
        this.payroll_integration.detailsForDefaults(planType).forEach(d => {result.push(d)});
        this.payroll_processing.detailsForDefaults(planType).forEach(d => {result.push(d)});
        this.custom_additions.detailsForDefaults(planType).forEach(d => {result.push(d)});
        this.custom_notes.detailsForDefaults(planType).forEach(d => {result.push(d)});
        return result;
    }
}

//######################################################
//### The classes that inherit ProposalPricingSection
//### represent the different components of the 
//### pricing section of the proposal builder page.
//######################################################

class PricingAssumptions extends ProposalPricingSection{
    constructor(data = {}){
        super(data);
        
        let detail = data.plan_assets || {};
        this.plan_assets = new ProposalPricingDetail(detail);

        detail = data.annual_contributions || {};
        this.annual_contributions = new ProposalPricingDetail(detail);

        detail = data.participants_with_balance || {};
        this.participants_with_balance = new ProposalPricingDetail(detail);
    }
    reset(){
        this.plan_assets.value = null;
    }
    isComplete(isTakeover){
        // console.log('PricingAssumptions.isComplete')
        let result = true;
        if (isTakeover){
            if (!this.plan_assets.value || !this.annual_contributions.value || !this.participants_with_balance.value){
                result = false;
            }
        } else {
            // this.plan_assets.value = null;
            if (!this.annual_contributions.value || !this.participants_with_balance.value){
                result = false;
            }
        }
        return result;
    }
    toJson(){
        // console.log('PricingAssumptions.toJson called');
        let result = this.superToJson();
        result.plan_assets = this.detailToJson(this.plan_assets);
        result.annual_contributions = this.detailToJson(this.annual_contributions);
        result.participants_with_balance = this.detailToJson(this.participants_with_balance);
        return result;
    }
}

class StandardFees extends ProposalPricingSection{
    constructor(data = {}){
        super(data);

        let detail = data.design_installation || {}
        this.design_installation = new ProposalPricingDetail(detail);

        detail = data.install_participant || {}
        this.install_participant = new ProposalPricingDetail(detail);

        detail = data.annual_participant || {}
        this.annual_participant = new ProposalPricingDetail(detail);

        detail = data.annual_admin || {}
        this.annual_admin = new ProposalPricingDetail(detail);

        detail = data.document || {}
        this.document = new ProposalPricingDetail(detail);
    }
    toJson(){
        // console.log('StandardFees.toJson called');
        let result = this.superToJson();
        result.design_installation = this.detailToJson(this.design_installation);
        result.install_participant = this.detailToJson(this.install_participant);
        result.annual_participant = this.detailToJson(this.annual_participant);
        result.annual_admin = this.detailToJson(this.annual_admin);
        result.document = this.detailToJson(this.document);
        return result;
    }
    detailsForDefaults(planType){
        let result = [];
        result.push(this.design_installation.forDefault('standard_fees',planType));
        result.push(this.install_participant.forDefault('standard_fees',planType));
        result.push(this.annual_participant.forDefault('standard_fees',planType));
        result.push(this.annual_admin.forDefault('standard_fees',planType));
        result.push(this.document.forDefault('standard_fees',planType));
        return result;
    }
}

class MEPPricing extends ProposalPricingSection{
    constructor(data = {}){
        super(data);
        
        let detail = data.plan_assets || {};
        this.plan_assets = new ProposalPricingDetail(detail);

        detail = data.annual_contributions || {};
        this.annual_contributions = new ProposalPricingDetail(detail);
        
        detail = data.sponsor_document || {};
        this.sponsor_document = new ProposalPricingDetail(detail);
        
        detail = data.adopting_employer || {};
        this.adopting_employer = new ProposalPricingDetail(detail);

        this.details = data.details || [];
        this.tiers = [];
        this.groups = [];
        this.loadTierPricing();
    }
    reset(){
        this.plan_assets.value = null;
    }
    get tierTotal(){
        return 200;
    }
    get participant_count(){
        let result = 0;
        this.groups.forEach(g => {
            result += safeInt(g.participant_count.value);
        });
        return result;
    }
    isComplete(isTakeover){
        // console.log('MEPPricing.isComplete');
        let result = true;
        if (isTakeover){
            if (!this.plan_assets.value || !this.annual_contributions.value){
                result = false;
            }
        } else {
            if (!this.annual_contributions.value){
                result = false;
            }
        }
        return result;
    }
    loadTierPricing(){
        if (this.details && this.details.length > 0){
            // console.log(this.section_name);
            for (let i = 1; i <= this.details.length / 5; i++){
                let group = {
                    'custom_group': i,
                    'tier_level': this.details.find(d => d.custom_group == `${i}` && d.detail_name == 'tier_level'),
                    'per_company': this.details.find(d => d.custom_group == `${i}` && d.detail_name == 'per_company'),
                    'company_count': this.details.find(d => d.custom_group == `${i}` && d.detail_name == 'company_count'),
                    'per_participant': this.details.find(d => d.custom_group == `${i}` && d.detail_name == 'per_participant'),
                    'participant_count': this.details.find(d => d.custom_group == `${i}` && d.detail_name == 'participant_count'),
                }
                this.groups.push(new MEPTierGroup(group));
            }
        } else {
            this.groups.push(new MEPTierGroup());
        }
    }
    toJson(){
        // console.log('MEPPricing.toJson called');
        let result = this.superToJson();
        result.plan_assets = this.detailToJson(this.plan_assets);
        result.annual_contributions = this.detailToJson(this.annual_contributions);
        result.sponsor_document = this.detailToJson(this.sponsor_document);
        result.adopting_employer = this.detailToJson(this.adopting_employer);
        result.details = [];
        this.groups.forEach(g => {
            result.details.push(this.detailToJson(g.tier_level));
            result.details.push(this.detailToJson(g.per_company));
            result.details.push(this.detailToJson(g.company_count));
            result.details.push(this.detailToJson(g.per_participant));
            result.details.push(this.detailToJson(g.participant_count));
        });
        return result;
    }
    detailsForDefaults(planType){
        let result = [];
        result.push(this.sponsor_document.forDefault('mep_pricing',planType));
        result.push(this.adopting_employer.forDefault('mep_pricing',planType));
        this.groups.forEach(g => {
            result.push(g.tier_level.forDefault('mep_pricing',planType));
            result.push(g.per_company.forDefault('mep_pricing',planType));
            result.push(g.per_participant.forDefault('mep_pricing',planType));
        })
        return result;
    }
}

export class MEPTierGroup{
    constructor(data = {}){
        this.custom_group =  data.custom_group || 1;

        let detail = data.tier_level || {'detail_name':'tier_level','custom_group':this.custom_group};
        this.tier_level = new ProposalPricingDetail(detail);

        detail = data.per_company || {'detail_name':'per_company','custom_group':this.custom_group}
        this.per_company = new ProposalPricingDetail(detail);

        detail = data.company_count || {'detail_name':'company_count','custom_group':this.custom_group}
        this.company_count = new ProposalPricingDetail(detail);

        detail = data.per_participant || {'detail_name':'per_participant','custom_group':this.custom_group}
        this.per_participant = new ProposalPricingDetail(detail);

        detail = data.participant_count || {'detail_name':'participant_count','custom_group':this.custom_group}
        this.participant_count = new ProposalPricingDetail(detail);
    }
    get total(){
        let perCo = this.per_company.value ? Number(safeInt(this.per_company.value)) : 0;
        let coCo = this.company_count.value ? Number(safeInt(this.company_count.value)) : 0;
        let perPa = this.per_participant.value ? Number(safeInt(this.per_participant.value)) : 0;
        let parCo = this.participant_count.value ? Number(safeInt(this.participant_count.value)) : 0;
        return (perCo * coCo) + (perPa * parCo);
    }
}

class PlanDocMaint extends ProposalPricingSection{
    constructor(data = {}){
        super(data);
        
        let detail = data.fee || {};
        this.fee = new ProposalPricingDetail(detail);
    }
    toJson(){
        // console.log('PlanDocMaint.toJson called');
        let result = this.superToJson();
        result.fee = this.detailToJson(this.fee);
        return result;
    }
    detailsForDefaults(planType){
        let result = [];
        result.push(this.fee.forDefault('plan_doc_maint',planType));
        return result;
    }
}

class AuditFee extends ProposalPricingSection{
    constructor(data = {}){
        super(data);
        
        let detail = data.fee || {};
        this.fee = new ProposalPricingDetail(detail);
    }
    toJson(){
        // console.log('AuditFee.toJson called');
        let result = this.superToJson();
        result.fee = this.detailToJson(this.fee);
        return result;
    }
    detailsForDefaults(planType){
        let result = [];
        result.push(this.fee.forDefault('audit_fee',planType));
        return result;
    }
}

class ThreeSixteen extends ProposalPricingSection{
    constructor(data = {}){
        super(data);
        this.three_sixteen_tier = data.three_sixteen_tier || null;

        let detail = data.use_basis_points || {};
        this.use_basis_points = new ProposalPricingDetail(detail);

        detail = data.basis_points || {};
        this.basis_points = new ProposalPricingDetail(detail);

        detail = data.service_tier || {};
        this.service_tier = new ProposalPricingDetail(detail);

        detail = data.calculate_on || {};
        this.calculate_on = new ProposalPricingDetail(detail);

        detail = data.setup_install || {};
        this.setup_install = new ProposalPricingDetail(detail);

        detail = data.annual_admin || {};
        this.annual_admin = new ProposalPricingDetail(detail);

        detail = data.annual_participant || {};
        this.annual_participant = new ProposalPricingDetail(detail);

        detail = data.participants_eligible || {};
        this.participants_eligible = new ProposalPricingDetail(detail);

        this.calculate_on_opts = [
            {'is_selected':false,'label':"Participants With A Balance",'value':'balance'},
            {'is_selected':false,'label':"Eligible Participants",'value':'eligible'}
        ]
        this.calculate_on_opt = this.calculate_on_opts[0];
    }
    setForMEP(){
        this.calculate_on_opt = this.calculate_on_opts[0];
    }
    toJson(){
        // console.log('ThreeSixteen.toJson');
        let result = this.superToJson();
        result.use_basis_points = this.detailToJson(this.use_basis_points);
        result.basis_points = this.detailToJson(this.basis_points);
        this.calculate_on.value = this.calculate_on_opt.value;
        result.calculate_on = this.detailToJson(this.calculate_on);
        result.service_tier = this.detailToJson(this.service_tier);
        result.setup_install = this.detailToJson(this.setup_install);
        result.annual_admin = this.detailToJson(this.annual_admin);
        result.annual_participant = this.detailToJson(this.annual_participant);
        result.participants_eligible = this.detailToJson(this.participants_eligible);
        // console.log(result);
        return result;
    }
    detailsForDefaults(planType){
        let result = [];
        result.push(this.use_basis_points.forDefault('three_sixteen',planType));
        result.push(this.basis_points.forDefault('three_sixteen',planType));
        result.push(this.service_tier.forDefault('three_sixteen',planType));
        result.push(this.calculate_on.forDefault('three_sixteen',planType));
        result.push(this.setup_install.forDefault('three_sixteen',planType));
        result.push(this.annual_admin.forDefault('three_sixteen',planType));
        result.push(this.annual_participant.forDefault('three_sixteen',planType));
        return result;
    }
}

class PayrollIntegration extends ProposalPricingSection{
    constructor(data = {}){
        super(data);

        let detail = data.pi_base_fee || {};
        this.pi_base_fee = new ProposalPricingDetail(detail);

        detail = data.pi_num_payroll || {};
        this.pi_num_payroll = new ProposalPricingDetail(detail);

        detail = data.pi_per_emp || {};
        this.pi_per_emp = new ProposalPricingDetail(detail);

        detail = data.pi_num_emp || {};
        this.pi_num_emp = new ProposalPricingDetail(detail);
    }
    get total(){
        let np = safeInt(this.pi_num_payroll.value);
        let pe = safeInt(this.pi_per_emp.value);
        let ne = safeInt(this.pi_num_emp.value);
        return (np*(pe*ne));
    }
    toJson(){
        // console.log('PayrollIntegration.toJson');
        let result = this.superToJson();
        result.pi_base_fee = this.detailToJson(this.pi_base_fee);
        result.pi_num_payroll = this.detailToJson(this.pi_num_payroll);
        result.pi_per_emp = this.detailToJson(this.pi_per_emp);
        result.pi_num_emp = this.detailToJson(this.pi_num_emp);
        return result;
    }
    detailsForDefaults(planType){
        let result = [];
        result.push(this.pi_base_fee.forDefault('payroll_integration',planType));
        result.push(this.pi_per_emp.forDefault('payroll_integration',planType));
        return result;
    }
}

class PayrollProcessing extends ProposalPricingSection{
    constructor(data = {}){
        super(data);

        let detail = data.unit_price || {};
        this.unit_price = new ProposalPricingDetail(detail);

        detail = data.quantity || {};
        this.quantity = new ProposalPricingDetail(detail);
    }
    get total(){
        let u = safeInt(this.unit_price.value);
        let q = safeInt(this.quantity.value);
        return u*q;
    }
    toJson(){
        // console.log('PayrollProcessing.toJson');
        let result = this.superToJson();
        result.unit_price = this.detailToJson(this.unit_price);
        result.quantity = this.detailToJson(this.quantity);
        return result;
    }
    detailsForDefaults(planType){
        let result = [];
        result.push(this.unit_price.forDefault('payroll_processing',planType));
        return result;
    }
}

class CustomAdditions extends ProposalPricingSection{
    constructor(data = {}){
        super(data);
        this.groups = [];
        this.description_options = [];
        if (data.details && data.details.length > 0){
            this.groups = new CustomGroup().getGroups(data.details);
            if (this.groups.length < 1){
                this.groups.push(new CustomGroup());
            }
        } else {
            this.groups = new CustomGroup().getDefaultGroups();
        }
    }
    get total(){
        let result = 0;
        this.groups.forEach(g => {
            if (g.is_checked.value){
                result += safeInt(g.value.value);
            }
        });
        return result;
    }
    toJson(){
        // console.log('CustomAdditions.toJson');
        let result = this.superToJson();
        result.details = [];
        this.groups.forEach(g => {
            result.details.push(this.detailToJson(g.is_checked));
            result.details.push(this.detailToJson(g.description));
            result.details.push(this.detailToJson(g.value));
        });
        return result;
    }
    detailsForDefaults(planType){
        let result = [];
        this.groups.forEach(g => {
            result.push(g.is_checked.forDefault('custom_additions',planType));
            result.push(g.description.forDefault('custom_additions',planType));
            result.push(g.value.forDefault('custom_additions',planType));
        })
        return result;
    }
}

class CustomNotes extends ProposalPricingSection{
    constructor(data = {}){
        super(data);
        this.groups = [];
        this.description_options = [];
        if (data.details && data.details.length > 0){
            this.groups = new CustomGroup().getGroups(data.details);
            if (this.groups.length < 1){
                this.groups.push(new CustomGroup());
            }
        } 
    }
    toJson(){
        // console.log('CustomNotes.toJson');
        let result = this.superToJson();
        result.details = [];
        this.groups.forEach(g => {
            result.details.push(this.detailToJson(g.is_checked));
            result.details.push(this.detailToJson(g.description));
            result.details.push(this.detailToJson(g.value));
        });
        return result;
    }
    detailsForDefaults(planType){
        let result = [];
        this.groups.forEach(g => {
            result.push(g.is_checked.forDefault('custom_notes',planType));
            result.push(g.description.forDefault('custom_notes',planType));
            result.push(g.value.forDefault('custom_notes',planType));
        })
        return result;
    }
}

export class CustomGroup{
    constructor(data = {}){
        this.custom_group = null;
        this.is_checked = false;
        this.description = ""
        this.value = ""

        if (data != {})
        {
            this.custom_group = data.custom_group || 1;
    
            let detail = data.is_checked || {'detail_name':'is_checked','custom_group':this.custom_group}
            this.is_checked = new ProposalPricingDetail(detail);
    
            detail = data.description || {'detail_name':'description','custom_group':this.custom_group}
            this.description = new ProposalPricingDetail(detail);
    
            detail = data.value || {'detail_name':'value','custom_group':this.custom_group}
            this.value = new ProposalPricingDetail(detail);
        }
    }
    getGroups(details){
        let result = [];
        for (let i = 1; i <= details.length / 3; i++){
            let group = {
                'custom_group': `${i}`,
                'is_checked': details.find(d => d.custom_group == `${i}` && d.detail_name == 'is_checked'),
                'description': details.find(d => d.custom_group == `${i}` && d.detail_name == 'description'),
                'value': details.find(d => d.custom_group == `${i}` && d.detail_name == 'value'),
            }
            result.push(new CustomGroup(group));
        }
        return result;
    }
    getDefaultGroups(){
        let result = [];
        for (let i = 1; i <= 3; i++){
            let group = {
                'custom_group': `${i}`,
                'is_checked': false,
                'description': "",
                'value': "",
            }
            result.push(new CustomGroup(group));
        }
        return result;
    }
}

class PBGC extends ProposalPricingSection{
    constructor(data = {}){
        super(data);
        this.pbgc_rates = data.pbgc_rates || new PBGCRates();
        
        let detail = data.premium_filing || {};
        this.premium_filing = new ProposalPricingDetail(detail);

        detail = data.per_participant || {}
        this.per_participant = new ProposalPricingDetail(detail);
    }
    setForMEP(isMEP = false){
        // console.log('PBGC.setForMEP');
        if (isMEP){
            this.per_participant.value = this.pbgc_rates.me_per_part_rate;
        } else {
            this.per_participant.value = this.pbgc_rates.se_per_part_rate;
        }
    }
    toJson(){
        // console.log('PBGC.toJson');
        let result = this.superToJson();
        result.premium_filing = this.detailToJson(this.premium_filing);
        result.per_participant = this.detailToJson(this.per_participant);
        return result;
    }
    detailsForDefaults(planType){
        let result = [];
        result.push(this.premium_filing.forDefault('pbgc',planType));
        return result;
    }
}

export class PricingOptions{
    constructor(data = {}){
        // console.log('PricingOptions.constructor')
        this.standard_fees = {
            design_installation: [],
            install_participant: [],
            annual_participant: [],
            annual_admin: [],
            document: []
        }
        if (data.standard_fees){
            if (data.standard_fees.design_installation) {
                this.standard_fees.design_installation = data.standard_fees.design_installation;
            }
            if (data.standard_fees.install_participant) {
                this.standard_fees.install_participant = data.standard_fees.install_participant;
            }
            if (data.standard_fees.annual_participant) {
                this.standard_fees.annual_participant = data.standard_fees.annual_participant;
            }
            if (data.standard_fees.annual_admin) {
                this.standard_fees.annual_admin = data.standard_fees.annual_admin;
            }
            if (data.standard_fees.document) {
                this.standard_fees.document = data.standard_fees.document;
            }
        }

        this.mep_pricing = {
            sponsor_document: [],
            adopting_employer: [],
            tiers: [],
            per_company: [],
            per_participant: []
        }
        if (data.mep_pricing){
            if (data.mep_pricing.sponsor_document) {
                this.mep_pricing.sponsor_document = data.mep_pricing.sponsor_document;
            }
            if (data.mep_pricing.adopting_employer) {
                this.mep_pricing.adopting_employer = data.mep_pricing.adopting_employer;
            }
            if (data.mep_pricing.tiers) {
                this.mep_pricing.tiers = data.mep_pricing.tiers;
            }
            if (data.mep_pricing.per_company) {
                this.mep_pricing.per_company = data.mep_pricing.per_company;
            }
            if (data.mep_pricing.per_participant) {
                this.mep_pricing.per_participant = data.mep_pricing.per_participant;
            }
        }

        this.plan_doc_maint = {
            fee: []
        }
        if (data.plan_doc_maint){
            if (data.plan_doc_maint.fee) {
                this.plan_doc_maint.fee = data.plan_doc_maint.fee;
            }
        }

        this.audit_fee = {
            fee: []
        }
        if (data.audit_fee){
            if (data.audit_fee.fee) {
                this.audit_fee.fee = data.audit_fee.fee;
            }
        }

        this.three_sixteen = {
            setup_install: [],
            annual_admin: [],
            annual_participant: [],
            service_tier: []
        }
        if (data.three_sixteen){
            if (data.three_sixteen.setup_install) {
                this.three_sixteen.setup_install = data.three_sixteen.setup_install;
            }
            if (data.three_sixteen.annual_admin) {
                this.three_sixteen.annual_admin = data.three_sixteen.annual_admin;
            }
            if (data.three_sixteen.annual_participant) {
                this.three_sixteen.annual_participant = data.three_sixteen.annual_participant;
            }
            if (data.three_sixteen.service_tier) {
                this.three_sixteen.service_tier = data.three_sixteen.service_tier;
            }
        }

        this.payroll_integration = {
            pi_base_fee: [],
            pi_per_emp: []
        }
        if (data.payroll_integration){
            if (data.payroll_integration.pi_base_fee) {
                this.payroll_integration.pi_base_fee = data.payroll_integration.pi_base_fee;
            }
            if (data.payroll_integration.pi_per_emp) {
                this.payroll_integration.pi_per_emp = data.payroll_integration.pi_per_emp;
            }
        }

        this.payroll_processing = {
            pay_process_per: [],
            pay_process_count: [],
            pay_process_annual: []
        }
        if (data.payroll_processing){
            if (data.payroll_processing.pay_process_per) {
                this.payroll_processing.pay_process_per = data.payroll_processing.pay_process_per;
            }
            if (data.payroll_processing.pay_process_count) {
                this.payroll_processing.pay_process_count = data.payroll_processing.pay_process_count;
            }
            if (data.payroll_processing.pay_process_annual) {
                this.payroll_processing.pay_process_annual = data.payroll_processing.pay_process_annual;
            }
        }

        this.custom_additions = {
            description: []
        }
        if (data.custom_additions){
            if (data.custom_additions.description) {
                this.custom_additions.description = data.custom_additions.description;
            }
        }

        this.custom_notes = {
            description: []
        }
        if (data.custom_notes){
            if (data.custom_notes.description) {
                this.custom_notes.description = data.custom_notes.description;
            }
        }
    }
}



export const createPricingTableItem = (data = {})=> {
   return  {
        id : data.id || uuidv4(),
        name : data.name || null,
        currentName : data.name || null,
        section : data.section || null,
        token : data.token || null,
        active : data.active || false,
        value : data.value || "",
    }
}
    

export  const createPricingTable =(data = {}) => {
    return {
        id : data.id || uuidv4(),
        pricing_table_id: data.pricing_table_id || null,
        userId : data.userId || null,
        tpaId : data.tpaId || null,
        name : data.name || null,
        entries : data.entries || null,
        isUserOnly: data.isUserOnly || true
    }
}
